import React, { useState, useEffect, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from './services/supabaseClient';
import { authService, clearSessionCache } from './services/authService';
import { User } from './types';
import { logger } from './lib/logger';
import { Login } from './features/auth/components/Login';
import { SignUp } from './features/auth/components/SignUp';
import { ForgotPassword } from './features/auth/components/ForgotPassword';
import { UpdatePassword } from './features/auth/components/UpdatePassword';
import { AuthCallback } from './features/auth/components/AuthCallback';
import { ProtectedRoute } from './features/auth/components/ProtectedRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Layout } from './components/layout/Layout';
import { Skeleton } from './components/ui/Skeleton';

// Lazy-loaded route components for code splitting
// These routes are loaded on-demand to reduce initial bundle size
const Dashboard = React.lazy(() => import('./features/dashboard/components/Dashboard').then(m => ({ default: m.Dashboard })));
const ComplexPage = React.lazy(() => import('./features/properties/pages/ComplexPage').then(m => ({ default: m.ComplexPage })));
const Onboarding = React.lazy(() => import('./features/onboarding/components/Onboarding').then(m => ({ default: m.Onboarding })));
const DocumentManagement = React.lazy(() => import('./features/documents/components/DocumentManagement').then(m => ({ default: m.DocumentManagement })));
const ProfileSettings = React.lazy(() => import('./features/profile/components/ProfileSettings').then(m => ({ default: m.ProfileSettings })));
const EmailAnalytics = React.lazy(() => import('./features/analytics/components/EmailAnalytics').then(m => ({ default: m.EmailAnalytics })));
const AdminDashboard = React.lazy(() => import('./features/admin/components/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const UserManagement = React.lazy(() => import('./features/dashboard/components/UserManagement').then(m => ({ default: m.UserManagement })));
const PropertiesPage = React.lazy(() => import('./features/properties/pages/PropertiesPage').then(m => ({ default: m.PropertiesPage })));
const LandingPage = React.lazy(() => import('./features/landing/components/LandingPage').then(m => ({ default: m.LandingPage })));

/**
 * Route loading fallback component
 * Shows a centered spinner while lazy-loaded routes are being fetched
 */
const RouteLoadingFallback: React.FC = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <div className="text-center">
      <div className="flex justify-center mb-4">
        <Skeleton variant="circular" width={48} height={48} animation="pulse" />
      </div>
      <Skeleton variant="text" width={120} height={16} className="mx-auto" />
    </div>
  </div>
);

const App = () => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  logger.debug('App rendering', { pathname: location.pathname });

  useEffect(() => {
    const checkSession = async () => {
      logger.debug('checkSession starting');
      try {
        const user = await authService.getCurrentUser();
        if (user) {
          setCurrentUser(user);
        } else {
          setCurrentUser(null);
        }
      } catch (error) {
        logger.error('Session check failed', error);
      } finally {
        setIsAuthChecking(false);
      }
    };

    // Add timeout to prevent hanging during E2E tests or network issues
    const timeoutId = setTimeout(() => {
      logger.warn('Auth check timed out after 5 seconds, proceeding without auth');
      setIsAuthChecking(false);
    }, 5000);

    checkSession().finally(() => {
      clearTimeout(timeoutId);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, _session) => {
      logger.debug('Auth state changed', { event });
      // Clear session cache on auth state changes to ensure fresh data
      if (event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        clearSessionCache();
      }
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        checkSession();
      } else if (event === 'SIGNED_OUT') {
        setCurrentUser(null);
      }
    });

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeoutId);
    };
  }, []);

  if (isAuthChecking) {
    return <div>Loading...</div>;
  }

  // Helper to check if user needs onboarding
  // SUPER_ADMIN users don't need onboarding even without a company_id
  const needsOnboarding = currentUser && !currentUser.companyId && currentUser.role !== 'SUPER_ADMIN';

  // Helper: redirects authenticated users away from public pages
  const PublicRoute = ({ children }: { children: React.ReactNode }) => {
    if (currentUser) {
      return <Navigate to={needsOnboarding ? "/onboarding" : "/dashboard"} replace />;
    }
    return <>{children}</>;
  };

  const handleMagicLink = async (email: string) => {
    try {
      return await authService.loginWithMagicLink(email);
    } catch (error: unknown) {
      logger.error('Magic link failed', error);
      throw error;
    }
  };

  const handleAuthCallback = async () => {
    try {
      const user = await authService.getCurrentUser();
      if (user) {
        setCurrentUser(user);
        // Navigate based on user state
        if (!user.companyId && user.role !== 'SUPER_ADMIN') {
          navigate('/onboarding');
        } else if (user.role === 'SUPER_ADMIN') {
          navigate('/admin');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (error: unknown) {
      logger.error('Auth callback failed', error);
    }
  };

  const handleSignUp = async (_email: string) => {
    // SignUp component handles the actual registration
    // This callback is called after successful signup to return to login
    navigate('/login');
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      setCurrentUser(null);
      navigate('/login');
    } catch (error) {
      logger.error('Logout failed', error);
    }
  };

  return (
    <ErrorBoundary>
      <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          <Route path="/" element={
            currentUser ? <Navigate to={needsOnboarding ? "/onboarding" : "/dashboard"} replace /> : <LandingPage />
          } />
          <Route path="/login" element={
            <PublicRoute><Login onMagicLink={handleMagicLink} /></PublicRoute>
          } />
          <Route path="/auth/callback" element={
            <AuthCallback onAuthSuccess={handleAuthCallback} />
          } />
          <Route path="/signup" element={
            <PublicRoute><SignUp onSignUp={handleSignUp} /></PublicRoute>
          } />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<UpdatePassword />} />

          {/* Protected Routes */}
          <Route element={
            <ProtectedRoute>
              {needsOnboarding ? <Navigate to="/onboarding" replace /> :
                <Layout
                  ownerName={currentUser?.name || 'User'}
                  onLogout={handleLogout}
                  isAdmin={currentUser?.role === 'SUPER_ADMIN'}
                  companyId={currentUser?.companyId}
                  userRole={currentUser?.role}
                />
              }
            </ProtectedRoute>
          }>
            <Route path="/dashboard" element={
              <ErrorBoundary featureName="Dashboard" showHome>
                <Dashboard
                  currentUser={currentUser}
                  companyId={currentUser?.companyId}
                  onViewDocuments={() => navigate('/documents')}
                  onViewDocumentDetail={(upload) => navigate(`/documents?id=${upload.id}`)}
                />
              </ErrorBoundary>
            } />
            <Route path="/properties" element={
              <ErrorBoundary featureName="Properties" showHome>
                <PropertiesPage />
              </ErrorBoundary>
            } />
            <Route path="/admin" element={
              <ErrorBoundary featureName="Admin Dashboard" showHome>
                <AdminDashboard />
              </ErrorBoundary>
            } />
            <Route path="/complex/:id" element={
              <ErrorBoundary featureName="Property Details" showHome>
                <ComplexPage />
              </ErrorBoundary>
            } />
            <Route path="/documents" element={
              <ErrorBoundary featureName="Documents" showHome>
                <DocumentManagement />
              </ErrorBoundary>
            } />
            <Route path="/profile" element={
              <ErrorBoundary featureName="Profile Settings" showHome>
                <ProfileSettings />
              </ErrorBoundary>
            } />
            <Route path="/analytics/emails" element={
              <ErrorBoundary featureName="Email Analytics" showHome>
                <EmailAnalytics />
              </ErrorBoundary>
            } />
            <Route path="/users" element={
              <ErrorBoundary featureName="Team Management" showHome>
                <UserManagement
                  companyId={currentUser?.companyId}
                  currentUser={currentUser ?? undefined}
                />
              </ErrorBoundary>
            } />
          </Route>

          <Route path="/onboarding" element={
            <ProtectedRoute>
              {needsOnboarding ? (
                <ErrorBoundary featureName="Onboarding" showHome>
                  <Onboarding />
                </ErrorBoundary>
              ) : <Navigate to="/dashboard" replace />}
            </ProtectedRoute>
          } />

          {/* Catch all */}
          <Route path="*" element={<Navigate to={currentUser?.role === 'SUPER_ADMIN' ? "/admin" : "/login"} replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
};

export default App;